<template>
  <div class="relative flex flex-col gap-2 p-3" :class="$style.root">
    <ColorArea v-model="hsl" />

    <div class="flex min-w-0 items-center gap-3">
      <div
        class="box-content size-6 rounded-md border border-border"
        :style="{ backgroundColor: modelValue || undefined }"
      ></div>

      <HueSlider v-model="hue" class="grow" />
    </div>

    <div class="flex min-w-0 items-center gap-2">
      <Menu.Root>
        <Menu.Trigger>
          <button
            type="button"
            aria-label="Select color space"
            class="shrink-0 rounded-md px-2.5 py-1.5 text-center font-mono text-sm font-medium uppercase outline-2 outline-blue-500 transition hover:bg-card focus-visible:outline"
          >
            {{ SPACE_LABELS[space] }}
          </button>
        </Menu.Trigger>

        <Menu.Container>
          <Menu.RadioGroup v-model="space">
            <Menu.RadioItem
              v-for="s in SPACES"
              :key="s"
              :value="s"
              :label="SPACE_LABELS[s]"
            />
          </Menu.RadioGroup>
        </Menu.Container>
      </Menu.Root>

      <HexColorInput v-if="space === 'hex'" v-model="hsl" />
      <RgbColorInput v-else-if="space === 'rgb'" v-model="hsl" />
      <HslColorInput v-else-if="space === 'hsl'" v-model="hsl" />
      <LchColorInput v-else v-model="hsl" />
    </div>

    <template v-if="!hidePresets">
      <hr class="my-1 border-border" />

      <ToggleGroupRoot
        :model-value="modelValue"
        type="single"
        class="grid grid-cols-8 gap-1.5"
        @update:model-value="onPresetChange"
      >
        <ToggleGroupItem
          v-for="preset in activePresets"
          :key="preset.color"
          :value="preset.color"
          :aria-label="preset.label"
          :style="{ backgroundColor: preset.color }"
          class="size-6 rounded-md border border-border outline-2 outline-offset-2 outline-blue-500 transition hover:scale-110 focus-visible:outline"
        />
      </ToggleGroupRoot>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  type Hsl,
  convertHslToRgb,
  convertRgbToHsl,
  formatHex,
  parseHex,
} from "culori/fn";
import type { AcceptableValue } from "reka-ui";
import { ToggleGroupItem, ToggleGroupRoot } from "reka-ui";
import { computed, shallowRef, watch } from "vue";

import Menu from "@/design_system/Menu";

import ColorArea from "./ColorArea.vue";
import { type ColorPreset, COLOR_PRESETS } from "./colors";
import HexColorInput from "./HexColorInput.vue";
import HslColorInput from "./HslColorInput.vue";
import HueSlider from "./HueSlider.vue";
import LchColorInput from "./LchColorInput.vue";
import RgbColorInput from "./RgbColorInput.vue";

const SPACES = ["hex", "rgb", "hsl", "lch"] as const;
type Space = (typeof SPACES)[number];

const SPACE_LABELS: Record<Space, string> = {
  hex: "Hex",
  rgb: "RGB",
  hsl: "HSL",
  lch: "LCH",
};

const props = defineProps<{
  hidePresets?: boolean;
  presets?: ColorPreset[];
}>();

const activePresets = computed(() => props.presets ?? COLOR_PRESETS);

const modelValue = defineModel<string>({ required: true });

const space = shallowRef<Space>("hex");
const hsl = shallowRef<Hsl>({ mode: "hsl", h: 0, s: 0, l: 0 });

const hue = computed({
  get: () => hsl.value.h ?? 0,
  set: (h: number) => {
    hsl.value = { ...hsl.value, h };
  },
});

const onPresetChange = (next: AcceptableValue | AcceptableValue[]) => {
  if (typeof next !== "string") return;
  modelValue.value = next;
};

let snapshot: string | undefined;

// Upstream hex → internal HSL
watch(
  modelValue,
  (hex) => {
    if (hex === snapshot) {
      return;
    }

    const rgb = parseHex(hex) ?? { mode: "rgb", r: 0, g: 0, b: 0 };

    snapshot = hex;
    hsl.value = convertRgbToHsl(rgb);
  },
  { immediate: true }
);

// Internal HSL → upstream hex
watch(hsl, (next) => {
  const hex = formatHex(convertHslToRgb(next));
  if (hex === snapshot) return;
  snapshot = hex;
  modelValue.value = hex;
});
</script>

<style module>
.root {
  width: 272px;
}
</style>
